import { css, keyframes } from 'styled-components';

export const menuFade = keyframes`
  from {opacity:0;}
  to {opacity:1;}
`;

export const menuEnter = keyframes`
  from { opacity:0; transform:translateY(-8px) scale(.96); }
  to { opacity:1; transform:translateY(0) scale(1); }
`;

/** Shell chrome shared by react-select menus and floating context menus. */
export const floatingMenuShellCss = css`
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text};
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.28);
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.controlRadius};
  box-sizing: border-box;
  overflow: hidden;
  padding: 6px;
  transform-origin: top center;
  animation: ${menuFade} 160ms ease-out;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const floatingMenuListCss = css`
  background: transparent;
  padding: 0;
  transform-origin: top center;
  animation: ${menuEnter} 160ms ease-out;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const floatingMenuItemCss = css`
  font-family: 'Archivo', sans-serif;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  padding: 8px 10px;
  min-height: 36px;
  border-radius: ${({ theme }) => theme.controlRadius};
  color: ${({ theme }) => theme.text};
  background: transparent;
  cursor: pointer;
  &:hover:not(:disabled),
  &[data-active='true'] {
    background: ${({ theme }) => theme.hover};
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

// Shared with the Club Switcher's menu: inset rows, subtle selection and compact spacing.
export const selectMenuCss = (prefix = 'react-select') => css`
  .${prefix}__menu {
    ${floatingMenuShellCss};
    z-index: 40;
    min-width: 100%;
    width: max-content;
  }
  .${prefix}__menu-list {
    ${floatingMenuListCss};
  }
  .${prefix}__option {
    ${floatingMenuItemCss};
    color: ${({ theme }) => theme.text} !important;
    background: transparent !important;
    &--is-focused,
    &--is-selected {
      background: ${({ theme }) => theme.hover} !important;
      color: ${({ theme }) => theme.text} !important;
    }
    &--is-selected::after {
      content: '✓';
      margin-left: auto;
      padding-left: 8px;
      color: ${({ theme }) => theme.headerAccent};
      font-weight: 700;
    }
    &--is-disabled {
      opacity: 0.45;
      cursor: default;
    }
  }
`;

export const themedSelectMenuCss = selectMenuCss();

/** Override react-select inline menu colors when menus portal to document.body.
 *  Pair with a createGlobalStyle that injects selectMenuCss on `__menu-portal`
 *  so enter animations still run (see ActionCell / StrategySortMenuStyle). */
export const portalSelectStyles = (theme, zIndex = 60) => ({
  menuPortal: (base) => ({
    ...base,
    zIndex,
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: theme.card,
    background: theme.card,
    border: `1px solid ${theme.border}`,
    borderRadius: theme.controlRadius,
    color: theme.text,
    boxShadow: '0 16px 40px rgba(0, 0, 0, 0.28)',
    padding: 6,
    overflow: 'hidden',
  }),
  menuList: (base) => ({
    ...base,
    background: 'transparent',
    padding: 0,
  }),
  option: (base, state) => ({
    ...base,
    color: theme.text,
    borderRadius: theme.controlRadius,
    backgroundColor:
      state.isFocused || state.isSelected ? theme.hover : 'transparent',
    cursor: state.isDisabled ? 'default' : 'pointer',
    fontFamily: 'Archivo, sans-serif',
    fontWeight: 500,
    fontSize: 13,
    padding: '8px 10px',
    minHeight: 36,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
});
